import React, { useState } from 'react'

import { ActiveSource, Source, SourceTable } from 'types/GlobalSource'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Switch from 'components_new/atoms/Switch'
import Text from 'components_new/atoms/Text'
import TooltipWrapper from 'components_new/atoms/TooltipWrapper'

import SegmentVirtualizedTable from 'components_new/molecules/SegmentVirtualizedTable'

import DeleteSourceTableDialog from 'components_new/organisms/internal/sources/DeleteSourceTableDialog'

import { getPreviewColumns, getPreviewRows, TSourceTablePreview } from './utils'

import { useSourceTableActions } from 'redux/actions/DataPlatform/connections/SourceTables'
import { useSourcesDeleting } from 'redux/hooks/Sources'
import { useSourceTablePreview } from 'redux/hooks/SourceTables'

interface SourceDataTableProps {
  activeSource: ActiveSource
  activeTable: SourceTable | null
  source: Source
}

const SourceDataTable = (props: SourceDataTableProps) => {
  const { activeSource, activeTable, source } = props

  const deleting = useSourcesDeleting()
  const { tryUpdateSourceTable } = useSourceTableActions()
  const preview = useSourceTablePreview(activeTable?.id ?? null) as
    | TSourceTablePreview
    | undefined

  const [deleteOpen, setDeleteOpen] = useState(false)

  /**
   * Whether a table is read into the platform is only a question for the
   * sources that deliver more than they need to — a file has nothing to opt
   * out of.
   */
  const canToggleLoad = source.attributes.type !== 'File'

  const notice = preview?.data.attributes.notice
  const columns = getPreviewColumns(preview?.data.attributes.columns)
  const rows = getPreviewRows(
    preview?.data.attributes.columns,
    preview?.data.included.preview
  )

  if (!activeTable) {
    return (
      <Text color="text.secondary" sx={{ p: 2 }} variant="body1">
        Välj en tabell i listan för att se dess data.
      </Text>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 0.5,
          height: 40,
          justifyContent: 'flex-end',
          px: 2
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          {canToggleLoad ? (
            <Box sx={{ alignItems: 'center', display: 'flex' }}>
              <Switch
                checked={Boolean(activeTable.attributes.load)}
                onChange={() =>
                  tryUpdateSourceTable(
                    activeTable.id,
                    {
                      data: {
                        attributes: {
                          columns: [],
                          load: !activeTable.attributes.load
                        }
                      }
                    },
                    activeSource.id
                  )
                }
              />
              <Text variant="body2">Läs in tabell</Text>
            </Box>
          ) : null}

          <TooltipWrapper title="Ta bort tabell">
            <IconButton
              disabled={deleting}
              onClick={() => setDeleteOpen(true)}
              size="small"
            >
              <Icon fontSize="small" name="Delete" />
            </IconButton>
          </TooltipWrapper>
        </Box>
      </Box>

      {notice ? (
        <Alert
          severity={notice.severity}
          square={true}
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Text gutterBottom={true} variant="subtitle2">
            {notice.title}
          </Text>
          <Text variant="body2">{notice.body}</Text>
          {notice.action ? (
            <Text sx={{ fontWeight: 'bold', mt: 1 }} variant="body2">
              {notice.action}
            </Text>
          ) : null}
        </Alert>
      ) : null}

      <SegmentVirtualizedTable
        columns={columns}
        data={rows}
        loading={!preview}
        sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
      />

      {/* borderTop before borderColor — the shorthand resets the colour. */}
      <Box
        sx={{ borderTop: '1px solid', borderColor: 'divider', px: 2, py: 1 }}
      >
        <Text color="text.secondary" variant="caption">
          {`Visar ${rows.length} av ${preview?.count ?? 0} rader`}
        </Text>
      </Box>

      <DeleteSourceTableDialog
        activeSource={activeSource}
        onClose={() => setDeleteOpen(false)}
        open={deleteOpen}
        table={activeTable}
      />
    </Box>
  )
}

export default SourceDataTable
